import { cva } from 'class-variance-authority';
import React, {
    ComponentPropsWithoutRef,
    ComponentType,
    useId,
    useLayoutEffect,
    useRef,
    useState,
} from 'react';

import { cn } from '@/services/utils';

import { Icon } from '../Icon';

type ListActionIcon = ComponentType<
    ComponentPropsWithoutRef<'svg'> & { title?: string }
>;

const listActionClassName = cva(
    [
        'tw-relative',
        'tw-box-border',
        'tw-flex',
        'tw-w-full',
        'tw-min-w-0',
        'tw-items-center',
        'tw-gap-2',
        'tw-h-9',
        'lg:tw-h-10',
        'tw-px-3',
        'tw-py-2',
        'tw-border-x-0',
        'tw-border-b-0',
        'tw-border-solid',
        'tw-bg-fill-primary',
        'tw-text-left',
        'tw-text-t-primary',
        'tw-cursor-pointer',
        'tw-transition-colors',
        'tw-duration-100',
        'tw-ease-out',
        'motion-reduce:tw-transition-none',
        'hover:tw-bg-fill-primary-hover',
        'active:tw-bg-fill-primary-press',
        'focus-visible:tw-z-10',
        'focus-visible:tw-outline-none',
        'focus-visible:tw-border-2',
        'focus-visible:tw-border-t-2',
        'focus-visible:tw-border-solid',
        'focus-visible:tw-border-primary-focus',
        'focus-visible:tw-bg-fill-primary-press',
        'disabled:tw-cursor-not-allowed',
        'disabled:tw-text-t-inactive',
        'disabled:hover:tw-bg-fill-primary',
        'disabled:active:tw-bg-fill-primary',
    ],
    {
        variants: {
            isFirstAction: {
                true: 'tw-border-t-0',
                false: 'tw-border-t tw-border-primary',
            },
        },
        defaultVariants: {
            isFirstAction: false,
        },
    },
);

export interface ListActionProps
    extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'title'> {
    /** Leading icon. Hidden when `showIcon` is false or when omitted. */
    icon?: ListActionIcon;
    leading?: React.ReactNode;
    /** Single-line label. Truncates, and the full text is shown in the native tooltip. */
    title: string;
    /** When false, the leading icon is hidden. Defaults to true. */
    showIcon?: boolean;
    /**
     * True for the first row in a stack. That row has no top divider.
     * Every following row draws a 1px top divider.
     */
    isFirstAction?: boolean;
    /** Blocks activation and uses the disabled appearance. */
    isDisabled?: boolean;
}

const ListAction = React.forwardRef<HTMLButtonElement, ListActionProps>(({
    icon,
    leading,
    title,
    showIcon = true,
    isFirstAction = false,
    isDisabled = false,
    className,
    disabled,
    type,
    'aria-describedby': ariaDescribedBy,
    ...props
}, ref) => {
    const titleRef = useRef<HTMLSpanElement>(null);
    const tooltipId = useId();
    const [isTruncated, setIsTruncated] = useState(false);
    const isButtonDisabled = isDisabled || Boolean(disabled);
    const describedBy = [
        ariaDescribedBy,
        isTruncated ? tooltipId : undefined,
    ].filter(Boolean).join(' ') || undefined;

    useLayoutEffect(() => {
        const element = titleRef.current;
        if (!element) {
            return undefined;
        }

        const measure = () => {
            const current = titleRef.current;
            if (!current) {
                return;
            }
            const truncated = current.scrollWidth > current.clientWidth;
            setIsTruncated((previous) => (previous === truncated ? previous : truncated));
        };

        measure();
        let cancelled = false;
        document.fonts?.ready.then(() => {
            if (!cancelled) {
                measure();
            }
        });

        if (typeof ResizeObserver === 'undefined') {
            return () => {
                cancelled = true;
            };
        }

        const observer = new ResizeObserver(measure);
        observer.observe(element);
        return () => {
            cancelled = true;
            observer.disconnect();
        };
    }, [title, showIcon, icon, leading, isTruncated]);

    const showLeading = showIcon && (Boolean(leading) || Boolean(icon));

    return (
        <>
            <button
                ref={ref}
                type={type ?? 'button'}
                disabled={isButtonDisabled}
                className={cn(listActionClassName({ isFirstAction }), className)}
                {...props}
                aria-describedby={describedBy}
                title={isTruncated ? title : undefined}
            >
                {showLeading ? (
                    <span className="tw-inline-flex tw-shrink-0" aria-hidden="true">
                        {leading ?? (icon ? (
                            <Icon
                                icon={icon}
                                disabled={isButtonDisabled}
                                className={cn(
                                    'tw-h-5 tw-w-5 lg:tw-h-6 lg:tw-w-6',
                                    isButtonDisabled ? 'tw-icon-inactive' : 'tw-icon-primary',
                                )}
                            />
                        ) : null)}
                    </span>
                ) : null}
                <span
                    ref={titleRef}
                    className="tw-min-w-0 tw-flex-1 tw-truncate tw-text-t-sm lg:tw-text-t-base"
                >
                    {title}
                </span>
            </button>
            {isTruncated ? (
                <span id={tooltipId} role="tooltip" className="tw-sr-only">
                    {title}
                </span>
            ) : null}
        </>
    );
});

ListAction.displayName = 'ListAction';

export { ListAction };
